import { useState } from "react";
import api from "@/lib/api";
import { PageHeader, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

function ScenarioCard({ title, data, tone, testid }) {
  const border = tone === "good" ? "border-emerald-300" : tone === "bad" ? "border-red-300" : "border-[#E2D9C8]";
  return (
    <div className={`border ${border} rounded-lg p-5 bg-white`} data-testid={testid}>
      <div className="label-eyebrow">{title}</div>
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-neutral-500">Ricavi</span><span className="font-medium">{fmtEUR(data.revenue)}</span></div>
        <div className="flex justify-between"><span className="text-neutral-500">Costi</span><span className="font-medium">{fmtEUR(data.cost)}</span></div>
        <div className="flex justify-between border-t border-[#E2D9C8] pt-2 mt-2">
          <span className="text-neutral-500">Utile</span>
          <span className={`font-serif text-xl ${data.profit < 0 ? "text-red-700" : "text-emerald-800"}`}>{fmtEUR(data.profit)}</span>
        </div>
        <div className="flex justify-between"><span className="text-neutral-500">Margine</span><span className="font-medium">{data.margin_pct}%</span></div>
      </div>
    </div>
  );
}

export default function Simulations() {
  const [inputs, setInputs] = useState({ price_change_pct: 5, cost_change_pct: 0, volume_change_pct: 0, staff_delta_monthly: 0 });
  const [ai, setAi] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/simulations/run", { ...inputs, include_ai_narrative: ai });
      setResult(data);
    } catch { toast.error("Errore simulazione"); }
    setLoading(false);
  };

  const presets = [
    { label: "Prezzi +5%", set: { price_change_pct: 5, cost_change_pct: 0, volume_change_pct: 0, staff_delta_monthly: 0 } },
    { label: "Materie prime +15%", set: { price_change_pct: 0, cost_change_pct: 15, volume_change_pct: 0, staff_delta_monthly: 0 } },
    { label: "Assumo cameriere (+1200€)", set: { price_change_pct: 0, cost_change_pct: 0, volume_change_pct: 5, staff_delta_monthly: 1200 } },
    { label: "Turisti -20%", set: { price_change_pct: 0, cost_change_pct: 0, volume_change_pct: -20, staff_delta_monthly: 0 } },
    { label: "Nuovo prodotto (+10% volume)", set: { price_change_pct: 0, cost_change_pct: 0, volume_change_pct: 10, staff_delta_monthly: 0 } },
  ];

  return (
    <div data-testid="simulations-page">
      <PageHeader eyebrow="Cosa succede se" title="Simulazioni"
        subtitle="Modifica le variabili e proietta ricavi, costi e margine sulla base degli ultimi 30 giorni." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-1 border border-[#E2D9C8] rounded-lg p-6 bg-white space-y-4">
          <div>
            <Label className="label-eyebrow">Variazione prezzi (%)</Label>
            <Input data-testid="sim-price" type="number" step="0.5" value={inputs.price_change_pct}
              onChange={(e) => setInputs({ ...inputs, price_change_pct: parseFloat(e.target.value || 0) })} />
          </div>
          <div>
            <Label className="label-eyebrow">Variazione materie prime (%)</Label>
            <Input data-testid="sim-cost" type="number" step="0.5" value={inputs.cost_change_pct}
              onChange={(e) => setInputs({ ...inputs, cost_change_pct: parseFloat(e.target.value || 0) })} />
          </div>
          <div>
            <Label className="label-eyebrow">Variazione volume (%)</Label>
            <Input data-testid="sim-volume" type="number" step="0.5" value={inputs.volume_change_pct}
              onChange={(e) => setInputs({ ...inputs, volume_change_pct: parseFloat(e.target.value || 0) })} />
          </div>
          <div>
            <Label className="label-eyebrow">Delta stipendio mensile (€)</Label>
            <Input data-testid="sim-staff" type="number" value={inputs.staff_delta_monthly}
              onChange={(e) => setInputs({ ...inputs, staff_delta_monthly: parseFloat(e.target.value || 0) })} />
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Checkbox data-testid="sim-ai" id="ai" checked={ai} onCheckedChange={(v) => setAi(!!v)} />
            <Label htmlFor="ai" className="text-sm">Includi analisi AI (Claude)</Label>
          </div>
          <Button data-testid="sim-run" onClick={run} disabled={loading}
            className="w-full bg-neutral-900 hover:bg-neutral-800 text-[#FDFBF7]">
            {loading ? "Calcolo…" : "Esegui simulazione"}
          </Button>
          <div className="pt-4 border-t border-[#E2D9C8]">
            <div className="label-eyebrow mb-2">Preset</div>
            <div className="space-y-1">
              {presets.map((p) => (
                <button key={p.label} onClick={() => setInputs(p.set)}
                  className="w-full text-left text-xs px-3 py-2 border border-[#E2D9C8] rounded hover:border-neutral-900 transition-colors">
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {!result && <div className="border border-dashed border-[#E2D9C8] rounded-lg p-12 text-center text-neutral-500">Imposta le variabili e clicca "Esegui simulazione".</div>}
          {result && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ScenarioCard title="Baseline (ultimi 30gg)" data={result.baseline} testid="scen-baseline" />
                <ScenarioCard title="Scenario applicato" data={result.scenario}
                  tone={result.scenario.profit > result.baseline.profit ? "good" : "bad"} testid="scen-scenario" />
                <ScenarioCard title="Pessimistico" data={result.pessimistic} tone="bad" testid="scen-pess" />
                <ScenarioCard title="Ottimistico" data={result.optimistic} tone="good" testid="scen-opt" />
              </div>
              <div className="border border-[#E2D9C8] rounded-lg p-6 bg-[#F3EDE1]">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="label-eyebrow">Break-even ricavi</div>
                    <div className="font-serif text-2xl">{result.break_even_revenue ? fmtEUR(result.break_even_revenue) : "n/d"}</div>
                    <div className="text-xs text-neutral-500 mt-1">Ricavo minimo per non perdere</div>
                  </div>
                  <div>
                    <div className="label-eyebrow">Impatto cash flow mensile</div>
                    <div className={`font-serif text-2xl ${result.cash_flow_impact_monthly < 0 ? "text-red-700" : "text-emerald-800"}`}>{fmtEUR(result.cash_flow_impact_monthly)}</div>
                    <div className="text-xs text-neutral-500 mt-1">vs baseline</div>
                  </div>
                </div>
              </div>
              {result.ai_narrative && (
                <div className="border border-[#E2D9C8] rounded-lg p-6 bg-white">
                  <div className="flex items-center gap-2 mb-3"><Sparkles size={14} strokeWidth={1.5} /><div className="label-eyebrow">Analisi AI</div></div>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{result.ai_narrative}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
